import { GetServerSidePropsContext } from "next";
import { generateNextSeo } from "next-seo/pages";
import Head from "next/head";
import Script from "next/script";
import {
  type Compilation,
  CompilationStatus,
} from "@memento/types/compilation";
import { DefaultLayout } from "src/layouts/DefaultLayout";
import { MementoPage } from "pages/_app";
import { ViewGroupVideo } from "src/containers/ViewGroupVideo";
import { Fetcher } from "src/libs/fetcher";
import { getQueryParam } from "src/libs/nextLib";
import {
  getCompilationEndpointUrl,
  getLatestCompilationEndpointUrl,
} from "src/libs/api-endpoint-lib";
import { AddReactionVideo } from "src/components/Reactions/AddReactionVideo";
import { useRouter } from "next/router";
import constants from "src/constants";
import { DisplayPublishedReactions } from "src/components/Reactions/DisplayPublishedReactions";
import { useCardReactionsPublic } from "src/hooks/useCardReactions";
import { addUrlParams } from "src/libs/urlLib";
import { isCardArchived } from "@memento/library/card";

function getIsViewable(compilation: Compilation | null) {
  return Boolean(
    compilation?.compilationStatus === CompilationStatus.COMPLETE &&
    compilation?.videoUrl
  );
}

type GroupVideoPageProps = MementoPage & {
  compilation: Compilation;
  latestCompilation: Compilation | null;
  isViewable: boolean;
  addReactionVideoAllowed: boolean;
};

// This is the page that shows the group video montage to users that want to view it.
// The compilation represents the montage video that was retrieved based on the ID in the URL.
// If that compilation is not viewable (it has been archived due to several more recent compilations
// being created), then we attempt to retrieve and refer the user to the latestCompilation instead.
export default function GroupVideoPage({
  compilation,
  latestCompilation,
  fullUrl,
  isViewable,
  addReactionVideoAllowed,
}: GroupVideoPageProps) {
  const compVideoUrl = compilation.videoCdnUrl;
  const compThumbUrl = compilation.thumbUrl;

  const titleText = compilation?.card?.title || "";

  const router = useRouter();
  const isRecipientView = Boolean(
    router.query[`${constants.reactionRecipientViewUrlParam}`]
  );

  const card = compilation.card;

  const { publishedReactions, pendingCount } = useCardReactionsPublic(
    card || null
  );

  if (!card) {
    return null;
  }

  const hasPublishedOrPendingReactions =
    publishedReactions.length > 0 || pendingCount > 0;

  const reactionsEnabledForRecipient =
    isRecipientView &&
    Boolean(card.reactionsEnabled) &&
    addReactionVideoAllowed &&
    isViewable;

  const recordUrl = addUrlParams(
    `/${constants.slugs.reactionLink}/${compilation.compilationId}/${constants.slugs.recordLink}`,
    isRecipientView ? { rp: 1 } : {}
  );

  return (
    <>
      <Head>
        {generateNextSeo({
          title: `Memento | ${titleText}`,
          noindex: true,
          openGraph: {
            type: "article",
            title: titleText,
            url: fullUrl,
            videos: [{ url: compVideoUrl, type: "video/mp4" }],
            images: [{ url: compThumbUrl }],
            article: {
              modifiedTime: new Date().toISOString(),
            },
          },
        })}
      </Head>
      {/* for the facebook "like" button */}
      <Script src="https://connect.facebook.net/en_US/sdk.js" />
      <DefaultLayout
        disableChat
        isPublicPage
        themeId={card?.themeId}
        nudgeFooter
        // don't show the footer ad
        showAdSlots={{ header: true, footer: false }}
      >
        <ViewGroupVideo
          fullUrl={fullUrl}
          compilation={compilation}
          isViewable={isViewable}
          latestCompilation={latestCompilation}
          isRecipientView={isRecipientView}
        />
        {reactionsEnabledForRecipient && !hasPublishedOrPendingReactions ? (
          <AddReactionVideo addReactionUrl={recordUrl} />
        ) : null}
        {isViewable ? (
          <DisplayPublishedReactions
            addVideoUrl={recordUrl}
            reactionType="Thank You"
            card={card}
            publishedReactions={publishedReactions}
            pendingCount={pendingCount}
            isRecipientView={isRecipientView}
            compilationId={compilation.compilationId}
            addReactionVideoAllowed={reactionsEnabledForRecipient}
          />
        ) : null}
      </DefaultLayout>
    </>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const compilationId = getQueryParam(ctx.query.firstId);

  const compilation: Compilation | null = await (async () => {
    if (!compilationId) {
      return null;
    }
    try {
      return await Fetcher()(getCompilationEndpointUrl(compilationId));
    } catch (err) {
      console.log(err);
      return null;
    }
  })();

  // No compilation was found for this ID
  if (!compilation) {
    return {
      notFound: true,
    };
  }

  const isViewable = getIsViewable(compilation);

  const latestCompilation: Compilation | null = await (async () => {
    if (!compilationId) {
      return null;
    }

    // If the compilation is viewable, we don't need to fetch others
    if (isViewable) {
      return null;
    }

    try {
      return await Fetcher()(getLatestCompilationEndpointUrl(compilationId));
    } catch (err) {
      console.log(err);
      return null;
    }
  })();

  const card = compilation.card;

  const addReactionVideoAllowed = Boolean(
    card && !isCardArchived(card) && card.reactionsEnabled
  );

  return {
    props: {
      compilation,
      latestCompilation,
      isViewable,
      addReactionVideoAllowed,
    },
  };
}
